import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import './Settings.css'

const NewPage = () => {
    const [activeTab, setActiveTab] = useState('overview')
    const [stats, setStats] = useState({
        wells: 0,
        users: 0,
        records: 0
    })

    useEffect(() => {
        if (activeTab === 'statistics') {
            fetchStats()
        }
    }, [activeTab])

    const fetchStats = async () => {
        try {
            // 1. 獲取水井總數
            const { count: wellsCount, error: wellsError } = await supabase
                .from('well')
                .select('*', { count: 'exact', head: true })

            // 2. 獲取使用者總數 (從 user_permissions 表)
            const { count: usersCount, error: usersError } = await supabase
                .from('user_permissions')
                .select('*', { count: 'exact', head: true })

            // 3. 獲取施工紀錄總數 (從 vendor_well 表)
            const { count: recordsCount, error: recordsError } = await supabase
                .from('vendor_well')
                .select('*', { count: 'exact', head: true })

            if (wellsError) console.error('Error fetching wells count:', wellsError)
            if (usersError) console.error('Error fetching users count:', usersError)
            if (recordsError) console.error('Error fetching records count:', recordsError)

            setStats({
                wells: wellsCount || 0,
                users: usersCount || 0,
                records: recordsCount || 0
            })
        } catch (error) {
            console.error('Error fetching statistics:', error)
        }
    }

    const [syncing, setSyncing] = useState(false)

    const handleSyncData = async () => {
        if (!window.confirm('確定要將施工紀錄的最新資料同步回水井基本資料嗎？\n這將會覆蓋現有的水井資料 (如馬力、馬達類型、深度等)。\n此操作無法復原。')) return

        try {
            setSyncing(true)

            // 1. 取得所有水井資料
            const { data: wells, error: wellError } = await supabase.from('well').select('*')
            if (wellError) throw wellError

            // 2. 取得所有施工紀錄 (按日期排序，確保最後處理的是最新的)
            const { data: records, error: recordError } = await supabase
                .from('vendor_well')
                .select('*')
                .order('repair_date', { ascending: true })

            if (recordError) throw recordError

            // 3. 整理出每個水井最新的施工紀錄
            const latestRecords = {}
            records.forEach(record => {
                if (record.well_id) {
                    latestRecords[record.well_id] = record
                }
            })

            let updatedCount = 0
            const updates = []

            // 4. 比對並準備更新
            // 定義要同步的欄位 (Vendor Well -> Well)
            const fieldsToSync = [
                'hp',
                'motor_type',
                'pump_depth',
                'well_tube_material',
                'static_water_level(m)',
                'outlet_pipe_diameter(inch)',
                'outlet_pipe_material',
                'standard_hydraulic_head_x_standard_water_volume',
                'vendor', // 廠商
                'repair_date', // 維修日期
                'failure_reason', // 故障原因
                'e_num', // 電號
                'unit' // 站別
            ]

            // 特殊欄位名稱映射 (Vendor Well key -> Well key)
            // 因為 Well 表可能有筆誤的欄位名稱 (空格 vs 底線)
            const fieldMapping = {
                'installation_depth_of_water_level_observation_pipe(m)': 'installation_depth_of water_level_observation_pipe(m)'
            }

            for (const well of wells) {
                const latestRecord = latestRecords[well.id]
                if (!latestRecord) continue

                const updatePayload = {}
                let hasChange = false

                // 檢查一般欄位
                fieldsToSync.forEach(field => {
                    // 只有當 Record 有值 (非 null/undefined/空字串) 且與 Well 不同時才更新
                    if (latestRecord[field] !== undefined &&
                        latestRecord[field] !== null &&
                        latestRecord[field] !== '' &&
                        latestRecord[field] != well[field]) { // 使用 != 允許類型寬鬆比較 (如 string vs number)

                        updatePayload[field] = latestRecord[field]
                        hasChange = true
                    }
                })

                // 檢查映射欄位
                Object.keys(fieldMapping).forEach(sourceKey => {
                    const targetKey = fieldMapping[sourceKey]
                    if (latestRecord[sourceKey] !== undefined &&
                        latestRecord[sourceKey] !== null &&
                        latestRecord[sourceKey] !== '' &&
                        latestRecord[sourceKey] != well[targetKey]) {

                        updatePayload[targetKey] = latestRecord[sourceKey]
                        hasChange = true
                    }
                })

                if (hasChange) {
                    // 執行更新
                    // 為了避免同時發送太多請求，我們將 Promise 加入陣列，稍後可以控制併發，或者簡單用 Promise.all
                    updates.push(
                        supabase.from('well').update(updatePayload).eq('id', well.id)
                    )
                    updatedCount++
                }
            }

            // 5. 執行所有更新
            if (updatedCount > 0) {
                // 簡單起見，直接執行所有請求 (如果數量過大可能需要分批)
                await Promise.all(updates)
                alert(`同步完成！共更新了 ${updatedCount} 筆水井資料。`)

                // 重新抓取統計資料
                fetchStats()
            } else {
                alert('沒有發現需要同步的資料 (現有資料已是最新)。')
            }

        } catch (error) {
            console.error('同步失敗:', error)
            alert(`同步失敗: ${error.message}`)
        } finally {
            setSyncing(false)
        }
    }

    const downloadCSV = (data, filename) => {
        if (!data || data.length === 0) {
            alert('沒有資料可匯出')
            return
        }

        // 取得所有欄位名稱 (以第一筆資料為準，或合併所有 keys)
        const headers = Object.keys(data[0])

        // 製作 CSV 內容
        const csvRows = []

        // 加入標題列
        csvRows.push(headers.join(','))

        // 加入資料列
        for (const row of data) {
            const values = headers.map(header => {
                const val = row[header]
                // 處理特殊字符：引號轉義，包含逗號的字串要用引號包起來
                const stringVal = val === null || val === undefined ? '' : String(val)
                const escaped = stringVal.replace(/"/g, '""')
                return `"${escaped}"`
            })
            csvRows.push(values.join(','))
        }

        const csvContent = csvRows.join('\n')

        // 加入 BOM (\uFEFF) 以讓 Excel 正確識別 UTF-8 中文
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleExportWells = async () => {
        try {
            const { data, error } = await supabase
                .from('well')
                .select('*')

            if (error) throw error

            const dateStr = new Date().toISOString().split('T')[0]
            downloadCSV(data, `水井資料匯出_${dateStr}.csv`)
        } catch (error) {
            console.error('匯出失敗:', error)
            alert('匯出失敗，請查看 Console 錯誤訊息')
        }
    }

    const handleExportRecords = async () => {
        try {
            const { data, error } = await supabase
                .from('vendor_well')
                .select('*')
                .order('repair_date', { ascending: false })

            if (error) throw error

            // 定義欄位對照表 (英文 -> 中文)
            const fieldMapping = {
                'name': '水井名稱',
                'unit': '站別',
                'e_num': '電號',
                'hp': '馬力',
                'motor_type': '馬達類型',
                'motor_vendor': '馬達廠牌',
                'pump_segment_number': '馬達段數',
                'standard_hydraulic_head_x_standard_water_volume': '標準揚程x標準水量',
                'outlet_pipe_diameter(inch)': '出水管徑(吋)',
                'outlet_pipe_material': '出水管材質',
                'pump_depth': 'pump安裝深度(m)',
                'well_tube_material': '井管材質',
                'installation_depth_of_water_level_observation_pipe(m)': '水位觀測管安裝深度(m)',
                'static_water_level(m)': '靜水位(m)',
                'failure_reason': '故障原因',
                'repair_date': '修復日期',
                'vendor': '廠商'
            }

            // 轉換資料格式
            const formattedData = data.map(item => {
                const newItem = {}
                Object.keys(fieldMapping).forEach(key => {
                    // 只包含在對照表中的欄位，這樣就能排除 id, well_id 等
                    newItem[fieldMapping[key]] = item[key]
                })
                return newItem
            })

            const dateStr = new Date().toISOString().split('T')[0]
            downloadCSV(formattedData, `施工紀錄匯出_${dateStr}.csv`)
        } catch (error) {
            console.error('匯出失敗:', error)
            alert('匯出失敗，請查看 Console 錯誤訊息')
        }
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="settings-section">
                        <h3>系統概覽</h3>
                        <div className="system-info">
                            <div className="info-item">
                                <span className="label">系統名稱：</span>
                                <span className="value">水井資訊管理系統</span>
                            </div>
                            <div className="info-item">
                                <span className="label">版本：</span>
                                <span className="value">v2.0.1</span>
                            </div>
                            <div className="info-item">
                                <span className="label">開發商：</span>
                                <span className="value">瑞翊工程有限公司</span>
                            </div>
                            <div className="info-item">
                                <span className="label">最後更新：</span>
                                <span className="value">2026-02-10</span>
                            </div>
                        </div>
                    </div>
                )
            case 'statistics':
                return (
                    <div className="settings-section">
                        <h3>統計資訊</h3>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-label">總水井數量:　</div>
                                <div className="stat-number">{stats.wells}</div>

                            </div>
                            <div className="stat-card">

                                <div className="stat-label">註冊用戶:　</div>
                                <div className="stat-number">{stats.users}</div>
                            </div>
                            <div className="stat-card">

                                <div className="stat-label">施工記錄:　</div>
                                <div className="stat-number">{stats.records}</div>
                            </div>
                        </div>
                    </div>
                )
            case 'tools':
                return (
                    <div className="settings-section">
                        <h3>工具箱</h3>
                        <div className="tools-grid">
                            <button className="tool-btn" onClick={handleExportWells}>
                                <div className="tool-title">匯出水井資料</div>
                                <div className="tool-desc">匯出所有水井資料為 csv 檔案</div>
                            </button>
                            <button className="tool-btn" onClick={handleExportRecords}>
                                <div className="tool-title">匯出施工紀錄</div>
                                <div className="tool-desc">匯出所有施工紀錄為 csv 檔案</div>
                            </button>
                            <button className="tool-btn" onClick={handleSyncData} disabled={syncing}>
                                <div className="tool-title">{syncing ? '同步中...' : '同步施工紀錄'}</div>
                                <div className="tool-desc">將最新的施工紀錄同步回水井基本資料</div>
                                <div className="tool-desc">按一次即可</div>
                            </button>

                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div className="settings">
            <div className="page-header">
                <h2>功能頁面</h2>
            </div>

            <div className="tab-navigation">
                <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    系統概覽
                </button>
                <button
                    className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('statistics')}
                >
                    統計資訊
                </button>
                <button
                    className={`tab-btn ${activeTab === 'tools' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tools')}
                >
                    工具箱
                </button>
            </div>

            <div className="settings-content">
                {renderTabContent()}
            </div>
        </div>
    )
}

export default NewPage
